import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { BookOpen, DownloadCloud, ExternalLink } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const ApiDocsSection = ({ onActionClick }) => {
  
  const handleNavigation = (url, ariaLabel) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="api-docs" className="py-20 bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-heading text-card-foreground">
            Dive Into Our <span className="gradient-text-brand">API</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Access comprehensive documentation, ready-to-use collections, and start building powerful PDF functionalities into your applications with ease.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ animate: { transition: { staggerChildren: 0.1 } } }}
        >
          <motion.div
            className="bg-background p-8 rounded-xl shadow-xl hover-lift border border-border flex flex-col text-center items-center"
            variants={fadeInUp}
          >
            <BookOpen className="h-12 w-12 text-primary mb-6" />
            <h3 className="text-2xl font-bold mb-3 text-primary-foreground font-heading">Public API Docs</h3>
            <p className="text-muted-foreground text-sm mb-6 flex-grow">
              Complete API reference and guides. Explore all endpoints, parameters, and example requests to get started quickly.
            </p>
            <Button 
              onClick={() => handleNavigation('https://apidocs.webfiny.com/', 'View WebFiny API Documentation')} 
              size="lg" 
              variant="outline" 
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground w-full md:w-auto group"
              aria-label="View WebFiny API Documentation in a new tab"
            >
              View Docs <ExternalLink className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>

          <motion.div
            className="bg-background p-8 rounded-xl shadow-xl hover-lift border border-border flex flex-col text-center items-center"
            variants={fadeInUp}
          >
            <DownloadCloud className="h-12 w-12 text-primary mb-6" />
            <h3 className="text-2xl font-bold mb-3 text-primary-foreground font-heading">Postman Collection</h3>
            <p className="text-muted-foreground text-sm mb-6 flex-grow">
              Ready-to-use API collection for quick testing and integration. Import into Postman and start making calls immediately.
            </p>
            <Button 
              onClick={() => handleNavigation('https://www.postman.com/finycore/webfiny', 'Get WebFiny Postman Collection')} 
              size="lg" 
              variant="outline" 
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground w-full md:w-auto group"
              aria-label="Get WebFiny Postman Collection in a new tab"
            >
              Get Collection <ExternalLink className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ApiDocsSection;